import React, { useState } from "react";
import { submitUserFormData, updateUser } from "../api/vipform";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function UserForm() {
  const [userformData, setuserFormData] = useState({
    name: "",
    username: "",
    password: "",
    email: "",
    contactno: "",
    status: "Active",
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  // Error states
  const [emailError, setEmailError] = useState("");
  const [contactError, setContactError] = useState("");

  const navigate = useNavigate();

  const handle_us_Edit = (user) => {
    setuserFormData(user);
    setIsEditMode(true);
    setSelectedUser(user);
    setEmailError("");
    setContactError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setuserFormData({
      name: "",
      username: "",
      password: "",
      email: "",
      contactno: "",
      status: "Active",
    });
    setIsEditMode(false);
    setSelectedUser(null);
    setEmailError("");
    setContactError("");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handle_us_Change = (e) => {
    const { name, value } = e.target;

    if (name === "contactno") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
      if (value.length === 1 && !/[5-9]/.test(value)) return;
      setContactError("");
    }

    if (name === "email") {
      if (value === "" || validateEmail(value)) {
        setEmailError("");
      }
    }

    setuserFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handle_us_Submit = async (e) => {
    e.preventDefault();

    if (!validateEmail(userformData.email)) {
      setEmailError("Please enter a valid email address (e.g., user@example.com)");
      toast.error("Invalid email format");
      return;
    }

    if (!/^[5-9]\d{9}$/.test(userformData.contactno)) {
      setContactError("Contact number must be 10 digits and start with 5–9");
      toast.error("Invalid contact number");
      return;
    }

    try {
      if (isEditMode) {
        await updateUser(userformData.id, userformData);
        toast.success("Manager details updated successfully!");
      } else {
        await submitUserFormData(userformData);
        toast.success("Manager registered successfully!");
        navigate("/userform");
      }
      resetForm();
      setRefreshTrigger((prev) => !prev);
    } catch (err) {
      console.error("Form submission failed:", err);
      toast.error("Submission failed. Please try again.");
    }
  };

  return (
    <>
      <div className="userform-wrapper">
        <div className="userform-card">
          {/* Header */}
          <div className="card-header">
            <div className="header-content">
              <h1 className="title">
                {isEditMode ? "Edit Manager Details" : "Manager Registration"}
              </h1>
              <p className="subtitle">
                {isEditMode
                  ? "Update manager information securely."
                  : "Create a new manager account with valid details."}
              </p>
            </div>
            <div className={`mode-badge ${isEditMode ? "edit" : "register"}`}>
              {isEditMode ? "Edit Mode" : "New Registration"}
            </div>
          </div>

          {/* Form Body */}
          <div className="card-body">
            <form onSubmit={handle_us_Submit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={userformData.name}
                    onChange={handle_us_Change}
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Username <span className="required">*</span></label>
                  <input
                    type="text"
                    name="username"
                    value={userformData.username}
                    onChange={handle_us_Change}
                    placeholder="Choose a username"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email Address <span className="required">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={userformData.email}
                    onChange={handle_us_Change}
                    placeholder="user@example.com"
                    className={emailError ? "error" : ""}
                    required
                  />
                  {emailError && <span className="error-text">{emailError}</span>}
                </div>

                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <input
                    type="password"
                    name="password"
                    value={userformData.password}
                    onChange={handle_us_Change}
                    placeholder="Enter strong password"
                    required
                  />
                </div>

                <div className="form-group full-width">
                  <label>Contact Number <span className="required">*</span></label>
                  <input
                    type="tel"
                    name="contactno"
                    value={userformData.contactno}
                    onChange={handle_us_Change}
                    placeholder="9876543210"
                    maxLength={10}
                    className={contactError ? "error" : ""}
                    required
                  />
                  {contactError && <span className="error-text">{contactError}</span>}
                  <small className="help-text">
                    Must be 10 digits, starting with 5–9
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button">
                  {isEditMode ? "Update Manager" : "Register Manager"}
                </button>

                {isEditMode && (
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .userform-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .userform-card {
          width: 100%;
          max-width: 900px;
          background: rgba(30, 41, 59, 0.9);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .card-header {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          padding: 40px;
          color: white;
          position: relative;
        }

        .header-content .title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color:#fff;
        }

        .header-content .subtitle {
          font-size: 1.1rem;
          opacity: 0.9;
          max-width: 600px;
        }

        .mode-badge {
          position: absolute;
          right: 30px;
          bottom: -20px;
          background: #f59e0b;
          color: white;
          padding: 10px 24px;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.95rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        .mode-badge.edit {
          background: #059669;
        }

        .card-body {
          padding: 50px 40px;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        /* ALL LABELS ARE NOW WHITE */
        .form-group label {
          font-weight: 600;
          color: white;                    /* ← Changed to pure white */
          margin-bottom: 8px;
          font-size: 1rem;
        }

        .required {
          color: #f87171;
        }

        .form-group input {
          padding: 16px 20px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input::placeholder {
          color: #94a3b8;
        }

        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
          background: rgba(15, 23, 42, 0.8);
        }

        .form-group input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
        }

        .error-text {
          color: #fca5a5;
          font-size: 0.875rem;
          margin-top: 6px;
        }

        .help-text {
          color: #94a3b8;
          font-size: 0.875rem;
          margin-top: 6px;
        }

        .form-actions {
          margin-top: 40px;
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .submit-button {
          flex: 1;
          min-width: 200px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border: none;
          padding: 18px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .submit-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4);
        }

        .cancel-button {
          background: rgba(100, 116, 139, 0.3);
          color: #e2e8f0;
          border: 2px solid rgba(148, 163, 184, 0.5);
          padding: 18px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-button:hover {
          background: rgba(148, 163, 184, 0.2);
          transform: translateY(-3px);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .card-header {
            padding: 30px 20px;
          }

          .header-content .title {
          color:#fff;
            font-size: 1.7rem;
          }

          .mode-badge {
            position: static;
            display: inline-block;
            margin-top: 20px;
          }

          .card-body {
            padding: 30px 20px;
          }

          .form-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .form-actions {
            flex-direction: column;
          }

          .submit-button,
          .cancel-button {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .userform-wrapper {
            padding: 10px;
          }

          .card-header {
            padding: 25px 15px;
          }

          .header-content .title {
          color:#fff;
            font-size: 1.5rem;
          }
        }
      `}</style>
    </>
  );
}
// import React, { useState } from "react";
// import FormInput from "./FormInput";
// import SubmitButton from "./SubmitButton";
// import { submitUserFormData, updateUser } from "../api/vipform";
// import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";

// export default function UserForm() {
//   const [userformData, setuserFormData] = useState({
//     name: "",
//     username: "",
//     password: "",
//     email: "",
//     contactno: "",
//     status: "Active",
//   });

//   const [isEditMode, setIsEditMode] = useState(false);
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [refreshTrigger, setRefreshTrigger] = useState(false);

//   const navigate = useNavigate();

//   const handle_us_Edit = (user) => {
//     setuserFormData(user);
//     setIsEditMode(true);
//     setSelectedUser(user);
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   };

//   const resetForm = () => {
//     setuserFormData({
//       name: "",
//       username: "",
//       password: "",
//       email: "",
//       contactno: "",
//       status: "Active",
//     });
//     setIsEditMode(false);
//     setSelectedUser(null);
//   };

//   // const handle_us_Change = (e) => {
//   //   const { name, value } = e.target;
//   //   setuserFormData((prev) => ({ ...prev, [name]: value }));
//   // };
//   const handle_us_Change = (e) => {
//     const { name, value } = e.target;

//     // ✅ Contact number rules
//     if (name === "contactno") {
//       // allow only digits
//       if (!/^\d*$/.test(value)) return;

//       // max 10 digits
//       if (value.length > 10) return;

//       // first digit must be 5–9
//       if (value.length === 1 && !/[5-9]/.test(value)) return;
//     }

//     setuserFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   // const handle_us_Submit = async (e) => {
//   //   e.preventDefault();
//   //   try {
//   //     if (isEditMode) {
//   //       await updateUser(userformData.id, userformData);
//   //       toast.success("User details updated successfully!");
//   //     } else {
//   //       await submitUserFormData(userformData);
//   //       navigate("/userform");
//   //     }
//   //     resetForm();
//   //     setRefreshTrigger((prev) => !prev);
//   //   } catch (err) {
//   //     console.error("Form submission failed:", err);
//   //     toast.error("Submission failed. Please try again.");
//   //   }
//   // };

//   const handle_us_Submit = async (e) => {
//     e.preventDefault();

//     // ✅ Final contact validation
//     if (!/^[5-9]\d{9}$/.test(userformData.contactno)) {
//       toast.error("Contact number must be 10 digits and start from 5–9");
//       return;
//     }

//     try {
//       if (isEditMode) {
//         await updateUser(userformData.id, userformData);
//         toast.success("User details updated successfully!");
//       } else {
//         await submitUserFormData(userformData);
//         navigate("/userform");
//       }
//       resetForm();
//       setRefreshTrigger((prev) => !prev);
//     } catch (err) {
//       console.error("Form submission failed:", err);
//       toast.error("Submission failed. Please try again.");
//     }
//   };

//   return (
//     <>
//       <style>{CSS}</style>

//       <div className="container">
//         <div className="registration-card">
//           {/* HEADER */}
//           <div className="header-section">
//             <div className="header-content">
//               <h1 className="header-title">
//                 {isEditMode
//                   ? "Edit Manager Details"
//                   : "Manager Registration Form"}
//               </h1>
//               <p className="header-description">
//                 {isEditMode
//                   ? "Update existing manager information."
//                   : "Register a new manager with required details."}
//               </p>
//             </div>

//             <div className={`mode-display ${isEditMode ? "edit-mode" : ""}`}>
//               {isEditMode ? "Edit Mode" : "Registration Mode"}
//             </div>
//           </div>

//           {/* FORM */}
//           <div className="form-section">
//             <form onSubmit={handle_us_Submit}>
//               <div className="form-layout">
//                 <div className="form-field">
//                   <label className="field-label">Full Name *</label>
//                   <input
//                     type="text"
//                     name="name"
//                     value={userformData.name}
//                     onChange={handle_us_Change}
//                     required
//                   />
//                 </div>

//                 <div className="form-field">
//                   <label className="field-label">Username *</label>
//                   <input
//                     type="text"
//                     name="username"
//                     value={userformData.username}
//                     onChange={handle_us_Change}
//                     required
//                   />
//                 </div>

//                 <div className="form-field">
//                   <label className="field-label">Email *</label>
//                   <input
//                     type="email"
//                     name="email"
//                     value={userformData.email}
//                     onChange={handle_us_Change}
//                     required
//                   />
//                 </div>

//                 <div className="form-field">
//                   <label className="field-label">Password *</label>
//                   <input
//                     type="text"
//                     name="password"
//                     value={userformData.password}
//                     onChange={handle_us_Change}
//                     required
//                   />
//                 </div>

//                 <div className="form-field full-width-field">
//                   <label className="field-label">Contact Number *</label>
//                   <input
//                     type="text"
//                     name="contactno"
//                     value={userformData.contactno}
//                     onChange={handle_us_Change}
//                     maxLength={10}
//                     pattern="[5-9][0-9]{9}"
//                     title="Enter a valid 10-digit mobile number starting from 5–9"
//                     required
//                   />
//                 </div>
//               </div>

//               <div className="actions-section">
//                 <button type="submit" className="submit-btn">
//                   {isEditMode ? "Update Manager" : "Register Manager"}
//                 </button>

//                 {isEditMode && (
//                   <button
//                     type="button"
//                     className="cancel-btn"
//                     onClick={resetForm}
//                   >
//                     Cancel Update
//                   </button>
//                 )}
//               </div>
//             </form>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// /* ------------------ SAME UI CSS AS VIPForm ------------------ */
// const CSS = `
// * { margin:0; padding:0; box-sizing:border-box; }

// .container { 
//   max-width:1100px; 
//   margin:10px auto; 
//   padding:20px; 
// }

// .registration-card {
//   background:linear-gradient(135deg, rgba(190,235,120,0.45), rgba(120,200,255,0.45));
//   border-radius:20px;
//   box-shadow:0 15px 40px rgba(0,0,0,.1);
//   overflow:hidden;
// }

// .header-section { 
//   background:linear-gradient(135deg,#1e3a8a,#3b82f6);
//   padding:35px 40px 45px;
//   color:#fff;
//   position:relative;
// }

// .header-title { 
//   font-size:32px; 
//   font-weight:700; 
//   color:#fff; 
// }

// .header-description {
//   margin-top:10px;
//   font-size:16px;
//   opacity:0.9;
// }

// .mode-display {
//   position:absolute;
//   right:40px;
//   bottom:-18px;
//   background:#f59e0b;
//   padding:10px 22px;
//   border-radius:50px;
//   color:white;
//   font-weight:600;
//   font-size:14px;
// }

// .mode-display.edit-mode { 
//   background:#059669; 
// }

// .form-section { 
//   padding:50px 40px; 
// }

// .form-layout {
//   display:grid;
//   grid-template-columns:repeat(2,1fr);
//   gap:28px;
// }

// .form-field { 
//   display:flex; 
//   flex-direction:column; 
// }

// .full-width-field { 
//   grid-column:span 2; 
// }

// .field-label {
//   margin-bottom:8px;
//   font-weight:600;
//   color:#374151;
// }

// input, select {
//   padding:15px 20px;
//   border:2px solid #e5e7eb;
//   border-radius:12px;
//   font-size:16px;
// }

// input:focus, select:focus {
//   outline:none;
//   border-color:#3b82f6;
// }

// .actions-section {
//   margin-top:40px;
//   display:flex;
//   gap:12px;
// }

// .submit-btn {
//   flex:1;
//   background:#2563eb;
//   color:white;
//   padding:18px;
//   border:none;
//   border-radius:12px;
//   font-size:16px;
//   font-weight:600;
//   cursor:pointer;
// }

// .submit-btn:hover {
//   background:#1d4ed8;
// }

// .cancel-btn {
//   background:#f3f4f6;
//   color:#374151;
//   padding:18px;
//   border-radius:12px;
//   border:2px solid #ddd;
//   font-size:16px;
//   font-weight:600;
//   cursor:pointer;
// }

// .have-account-btn {
//   background: white;
//   color: #1e73be;
//   border: 2px solid #1e73be;
//   padding: 12px 30px;
//   border-radius: 8px;
//   font-size: 16px;
//   font-weight: 600;
//   cursor: pointer;
// }

// .have-account-btn:hover {
//   background: #1e73be;
//   color: white;
// }
// `;
